/**
 * The console's typed API client, built from `staffRoutes` (08 §7). One function, `call(route, req)`,
 * fills `:params`, appends the query, sends JSON (or multipart) with the staff cookie and, on every
 * non-GET, the `x-becca-csrf` header copied from the `becca_csrf` cookie (08 §5). Errors arrive as
 * `ApiError` carrying the API's `{ error: { code, message, details } }` body.
 *
 * `VITE_FAKE_API=1` swaps the fetch transport for the in-memory fake in `./fake` (same contracts).
 *
 * Phase 1 adds `agentApi` (one function per `staffAgentRoutes` entry) and the concierge SSE reader the
 * Try it page uses (`streamConcierge`, `fetch` + `ReadableStream`, the session's guest token).
 */
import {
  buildPath,
  buildQuery,
  ConciergeHistory,
  ConciergeSseEvent,
  ErrorBody,
  guestAgentRoutes,
  staffAgentRoutes,
  STAFF_CSRF_COOKIE,
  STAFF_CSRF_HEADER,
  type ErrorCode,
  type HttpMethod,
  type RouteDef,
  type RouteRequest,
  type RouteResponse,
  type ValidationIssue,
  type AgentSettingsInput,
  type AiCostQuery,
  type CaseQuery,
  type ConciergeSendBody,
  type CreateTryItBody,
  type FlagTurnBody,
  type PutGrantsBody,
} from '@becca/contracts';

export interface TransportResult {
  status: number;
  /** Parsed JSON body, or null for an empty body. */
  body: unknown;
}

export interface TransportRequest {
  /** Already-built query string, `?a=1` or empty. */
  query: string;
  body?: unknown;
}

/** How a request reaches the API: `fetch` in the browser, the fake in tests and `VITE_FAKE_API=1`. */
export type Transport = (method: HttpMethod, path: string, req: TransportRequest) => Promise<TransportResult>;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | string,
    message: string,
    readonly details: unknown = undefined,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** `VALIDATION_FAILED` issues, keyed by dotted path (`hours.mon.0.close`), for forms. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    if (!Array.isArray(this.details)) return out;
    for (const issue of this.details as ValidationIssue[]) {
      if (!issue || !Array.isArray(issue.path)) continue;
      const key = issue.path.join('.');
      if (!(key in out)) out[key] = issue.message;
    }
    return out;
  }
}

export const isApiError = (err: unknown): err is ApiError => err instanceof ApiError;

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  for (const part of document.cookie.split(';')) {
    const [k, ...rest] = part.trim().split('=');
    if (k === name) return decodeURIComponent(rest.join('='));
  }
  return null;
}

/** In dev, Vite proxies `/s/*` and `/files/*` to the API, so paths are relative and the cookie first-party. */
export const fetchTransport: Transport = async (method, path, req) => {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (method !== 'GET') headers[STAFF_CSRF_HEADER] = readCookie(STAFF_CSRF_COOKIE) ?? '';
  let body: BodyInit | undefined;
  if (req.body instanceof FormData) body = req.body;
  else if (req.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(req.body);
  }
  const res = await fetch(`${path}${req.query}`, { method, headers, body, credentials: 'same-origin' });
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = { error: { code: 'INTERNAL', message: text.slice(0, 200) } };
    }
  }
  return { status: res.status, body: parsed };
};

let transport: Transport | null = null;

/** Tests and the fake mode install their transport here; the browser default is `fetch`. */
export function setTransport(t: Transport): void {
  transport = t;
}

async function resolveTransport(): Promise<Transport> {
  if (transport) return transport;
  if (import.meta.env.VITE_FAKE_API === '1') {
    const { fakeTransport } = await import('./fake/index');
    transport = fakeTransport;
  } else {
    transport = fetchTransport;
  }
  return transport;
}

/** Send one request described by a `staffRoutes` entry. Resolves with the response body on success. */
export async function call<R extends RouteDef>(route: R, req: RouteRequest<R>): Promise<RouteResponse<R>> {
  const t = await resolveTransport();
  const params = (req as { params?: Record<string, string | number> }).params ?? {};
  const query = buildQuery((req as { query?: Record<string, unknown> }).query);
  const body = (req as { body?: unknown }).body;
  const result = await t(route.method, buildPath(route.path, params), { query, body });
  // `auth.verify` answers 302 by contract; any declared success status counts.
  if ((result.status >= 200 && result.status < 300) || result.status === route.status) {
    return result.body as RouteResponse<R>;
  }
  const parsed = ErrorBody.safeParse(result.body);
  if (parsed.success) {
    const { code, message, details } = parsed.data.error;
    throw new ApiError(result.status, code, message, details);
  }
  throw new ApiError(result.status, 'INTERNAL', `Request failed with status ${result.status}`);
}

// ── Concierge agent (phase 1, 10 §3, §6) ──────────────────────────────────

/**
 * One named function per `staffAgentRoutes` entry, so a screen or a test can say
 * `agentApi.take(leadId)` instead of spelling the route. They all go through `call()`.
 */
export const agentApi = {
  case: (leadId: string, query: CaseQuery = {}) => call(staffAgentRoutes.leadAgent.case, { params: { id: leadId }, query }),
  instruct: (leadId: string, text: string) =>
    call(staffAgentRoutes.leadAgent.instruct, { params: { id: leadId }, body: { text } }),
  take: (leadId: string) => call(staffAgentRoutes.leadAgent.take, { params: { id: leadId } }),
  handback: (leadId: string) => call(staffAgentRoutes.leadAgent.handback, { params: { id: leadId } }),
  reply: (leadId: string, text: string) =>
    call(staffAgentRoutes.leadAgent.reply, { params: { id: leadId }, body: { text } }),
  getSettings: () => call(staffAgentRoutes.agentSettings.get, {}),
  putSettings: (body: AgentSettingsInput) => call(staffAgentRoutes.agentSettings.put, { body }),
  listGrants: () => call(staffAgentRoutes.agentGrants.list, {}),
  putGrants: (body: PutGrantsBody) => call(staffAgentRoutes.agentGrants.put, { body }),
  createTryIt: (body: CreateTryItBody = {}) => call(staffAgentRoutes.tryIt.create, { body }),
  deleteTryIt: (sessionId: string) => call(staffAgentRoutes.tryIt.delete, { params: { id: sessionId } }),
  flagTurn: (sessionId: string, body: FlagTurnBody) =>
    call(staffAgentRoutes.tryIt.flag, { params: { id: sessionId }, body }),
  aiCost: (query: AiCostQuery) => call(staffAgentRoutes.reports.aiCost, { query }),
} satisfies Record<string, (...args: never[]) => Promise<unknown>>;

/**
 * The guest concierge as Try it uses it: `POST /g/concierge` (SSE) and `GET /g/concierge` with the
 * Try-it session's guest token as `Authorization: Bearer`. Staff never have a guest token of a real
 * lead; the token here belongs to the synthetic lead the session created.
 */
export interface ConciergeTransport {
  send(token: string, body: ConciergeSendBody, signal?: AbortSignal): AsyncIterable<ConciergeSseEvent>;
  history(token: string): Promise<ConciergeHistory>;
}

/** One SSE frame: `event:` (default `message`) and the joined `data:` lines. */
export interface SseFrame {
  event: string;
  data: string;
}

/**
 * Split a `text/event-stream` body into frames. Handles CRLF, frames split across chunks, multi-line
 * `data:`, comments (`: keep-alive`) and a final frame without the trailing blank line.
 */
export async function* readSseFrames(stream: ReadableStream<Uint8Array>): AsyncGenerator<SseFrame> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let event = '';
  let data: string[] = [];
  const flush = (): SseFrame | null => {
    const frame = data.length ? { event: event || 'message', data: data.join('\n') } : null;
    event = '';
    data = [];
    return frame;
  };
  /** One line of the stream; a blank line ends the frame and returns it. */
  const takeLine = (line: string): SseFrame | null => {
    if (line === '') return flush();
    if (line.startsWith(':')) return null; // comment / keep-alive
    const colon = line.indexOf(':');
    const field = colon < 0 ? line : line.slice(0, colon);
    const raw = colon < 0 ? '' : line.slice(colon + 1);
    const val = raw.startsWith(' ') ? raw.slice(1) : raw;
    if (field === 'event') event = val;
    else if (field === 'data') data.push(val);
    return null;
  };
  try {
    for (;;) {
      const { value, done } = await reader.read();
      buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buffer.search(/\r\n|\r|\n/)) >= 0) {
        // A lone CR at the end of a chunk may be half of a CRLF split across chunks.
        if (!done && nl === buffer.length - 1 && buffer[nl] === '\r') break;
        const line = buffer.slice(0, nl);
        buffer = buffer.slice(nl + (buffer.startsWith('\r\n', nl) ? 2 : 1));
        const frame = takeLine(line);
        if (frame) yield frame;
      }
      if (done) break;
    }
    if (buffer) takeLine(buffer);
    const last = flush();
    if (last) yield last;
  } finally {
    reader.releaseLock();
  }
}

/** Frames → typed events. A frame that fails the contract becomes `error { code: BAD_EVENT }`. */
export async function* readConciergeEvents(stream: ReadableStream<Uint8Array>): AsyncGenerator<ConciergeSseEvent> {
  for await (const frame of readSseFrames(stream)) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(frame.data);
    } catch {
      yield { type: 'error', code: 'BAD_EVENT' };
      continue;
    }
    const ev = ConciergeSseEvent.safeParse(parsed);
    yield ev.success ? ev.data : { type: 'error', code: 'BAD_EVENT' };
  }
}

async function guestError(res: Response): Promise<ApiError> {
  const text = await res.text().catch(() => '');
  try {
    const parsed = ErrorBody.safeParse(JSON.parse(text));
    if (parsed.success) {
      const { code, message, details } = parsed.data.error;
      return new ApiError(res.status, code, message, details);
    }
  } catch {
    // not JSON
  }
  return new ApiError(res.status, 'INTERNAL', `Request failed with status ${res.status}`);
}

/** `fetch` + `ReadableStream` (EventSource cannot send the bearer header). Vite proxies `/g/*` in dev. */
export const fetchConciergeTransport: ConciergeTransport = {
  async *send(token, body, signal) {
    const route = guestAgentRoutes.conciergeSend;
    const res = await fetch(route.path, {
      method: route.method,
      headers: {
        accept: 'text/event-stream',
        'content-type': 'application/json',
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal,
    });
    if (!res.ok) throw await guestError(res);
    if (!res.body) throw new ApiError(res.status, 'INTERNAL', 'The concierge answered without a stream');
    yield* readConciergeEvents(res.body);
  },
  async history(token) {
    const route = guestAgentRoutes.conciergeHistory;
    const res = await fetch(route.path, {
      method: route.method,
      headers: { accept: 'application/json', authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw await guestError(res);
    return ConciergeHistory.parse(await res.json());
  },
};

let conciergeTransport: ConciergeTransport | null = null;

/** Tests and the fake mode install the scripted concierge here. */
export function setConciergeTransport(t: ConciergeTransport): void {
  conciergeTransport = t;
}

async function resolveConcierge(): Promise<ConciergeTransport> {
  if (conciergeTransport) return conciergeTransport;
  if (import.meta.env.VITE_FAKE_API === '1') {
    const { fakeConciergeTransport } = await import('./fake/index');
    conciergeTransport = fakeConciergeTransport;
  } else {
    conciergeTransport = fetchConciergeTransport;
  }
  return conciergeTransport;
}

/** Send one guest-side message to the concierge and iterate its SSE events until `done`/`error`. */
export async function* streamConcierge(
  token: string,
  body: ConciergeSendBody,
  signal?: AbortSignal,
): AsyncGenerator<ConciergeSseEvent> {
  const t = await resolveConcierge();
  yield* t.send(token, body, signal);
}

export async function conciergeHistory(token: string): Promise<ConciergeHistory> {
  return (await resolveConcierge()).history(token);
}
